"use client";

import type { Locale } from "date-fns";
import { useState } from "react";
import { Input } from "../../elements/input/input";
import { Label } from "../../elements/label/label";
import { Text } from "../../elements/text/text";
import { DatePicker } from "../../patterns/date-picker/date-picker";
import { cn } from "../../utils/cn";
import { AddressSelector } from "../address-selector/address-selector";

export type AddressData = {
  id: string;
  isDefault?: boolean;
  companyName: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

export type DeliveryOption = {
  id: string;
  label: string;
  description?: string;
  price?: string;
  estimatedDays?: string;
};

export type PaymentMethod = {
  id: string;
  label: string;
  description?: string;
};

export type DeliveryPaymentInfoProps = {
  country?: string | undefined;
  shippingAddresses?: AddressData[];
  billingAddresses?: AddressData[];
  deliveryOptions: DeliveryOption[];
  selectedDeliveryOption?: string;
  onDeliveryOptionChange?: (optionId: string) => void;
  requestedDeliveryDate?: Date;
  onDeliveryDateChange?: (date: Date | undefined) => void;
  minDeliveryDate?: Date;
  maxDeliveryDate?: Date;
  paymentMethods?: PaymentMethod[];
  selectedPaymentMethod?: string;
  onPaymentMethodChange?: (methodId: string) => void;
  onChangeShippingAddress?: (address: AddressData) => Promise<{ ok: boolean }>;
  onChangeBillingAddress?: (address: AddressData) => Promise<{ ok: boolean }>;
  locale: Partial<Locale>;
  className?: string;
  orderNote?: string;
  onOrderNoteChange?: (note: string) => void;
  personalReferenceMaxLength?: number;
  onPersonalReferenceChange?: (value: string) => void;
  readonly?: boolean;
  microcopies: {
    title: string;
    shippingAddressLabel: string;
    billingAddressLabel: string;
    deliveryOptionsLabel: string;
    selectedDeliveryOptionLabel: string;
    paymentMethodLabel: string;
    selectedPaymentOptionLabel: string;
    changeAddressButton: string;
    noAddressProvided: string;
    optionalLabel: string;
    personalReferenceLabel: string;
    personalReferencePlaceholder: string;
    requestedShippingDateLabel: string;
    dateLabel: string;
    noteToCustomerServiceLabel: string;
    noteToCustomerServiceDescription: string;
    characterCounter: string;
    deliveryOptionsDescription?: string;
    paymentOptionsDescription?: string;
    addressDialogWarningTitle: string;
    addressDialogWarningDescription: string;
    addressDialogTitle: string;
    addressDialogSubtitle: string;
    addressDialogSubmitButtonLabel: string;
    addressDialogErrorMessage: string;
  };
  "data-testid"?: string;
};

export function DeliveryPaymentInfo(props: Readonly<DeliveryPaymentInfoProps>) {
  const personalReferenceMaxLength = props.personalReferenceMaxLength ?? 24;
  const [internalNote, setInternalNote] = useState(props.orderNote ?? "");
  const [internalReference, setInternalReference] = useState("");

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newNote = e.target.value;
    setInternalNote(newNote);
    props.onOrderNoteChange?.(newNote);
  };

  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newReference = e.target.value.slice(0, personalReferenceMaxLength);
    setInternalReference(newReference);
    props.onPersonalReferenceChange?.(newReference);
  };

  const showOptionalSection = Boolean(
    props.onOrderNoteChange ||
      props.onPersonalReferenceChange ||
      props.onDeliveryDateChange
  );

  return (
    <div
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-md border border-stone-200 bg-white",
        props.className
      )}
      data-testid={props["data-testid"]}
    >
      {/* Header */}
      <div className="border-stone-200 border-b px-4 py-4">
        <h2 className="font-semibold text-lg text-stone-900 leading-[1.56]">
          {props.microcopies.title}
        </h2>
      </div>

      {/* Content Row 1 - 4 Columns */}
      <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-4">
        {/* Shipping Address */}
        <div className="flex flex-col gap-2">
          <Label className="font-semibold text-base">
            {props.microcopies.shippingAddressLabel}
          </Label>
          <AddressSelector
            addresses={props.shippingAddresses}
            microcopies={{
              changeAddressButtonLabel: props.microcopies.changeAddressButton,
              warningTitle: props.microcopies.addressDialogWarningTitle,
              warningDescription:
                props.microcopies.addressDialogWarningDescription,
              dialogTitle: props.microcopies.addressDialogTitle,
              dialogSubtitle: props.microcopies.addressDialogSubtitle,
              dialogSubmitButtonLabel:
                props.microcopies.addressDialogSubmitButtonLabel,
              dialogErrorMessage: props.microcopies.addressDialogErrorMessage,
            }}
            onSelectAddress={props.onChangeShippingAddress}
          />
        </div>

        {/* Billing Address */}
        <div className="flex flex-col gap-2">
          <Label className="font-semibold text-base">
            {props.microcopies.billingAddressLabel}
          </Label>
          <AddressSelector
            addresses={props.billingAddresses}
            microcopies={{
              changeAddressButtonLabel: props.microcopies.changeAddressButton,
              warningTitle: props.microcopies.addressDialogWarningTitle,
              warningDescription:
                props.microcopies.addressDialogWarningDescription,
              dialogTitle: props.microcopies.addressDialogTitle,
              dialogSubtitle: props.microcopies.addressDialogSubtitle,
              dialogSubmitButtonLabel:
                props.microcopies.addressDialogSubmitButtonLabel,
              dialogErrorMessage: props.microcopies.addressDialogErrorMessage,
            }}
            onSelectAddress={props.onChangeBillingAddress}
            shouldShowCTA={false}
          />
        </div>

        {/* Delivery Options */}
        <div className="flex flex-col gap-2">
          <Label className="font-semibold text-base">
            {props.microcopies.deliveryOptionsLabel}
          </Label>
          {props.microcopies.deliveryOptionsDescription && (
            <div className="max-w-[210px]">
              <Text variant={"body-sm"}>
                {props.microcopies.deliveryOptionsDescription}
              </Text>
            </div>
          )}
          {/* Radio Options */}
          <div className="flex flex-col gap-2">
            {props.deliveryOptions.map((option) => (
              <Label
                className="flex cursor-pointer items-center gap-2 font-normal"
                htmlFor={`delivery-${option.id}`}
                key={option.id}
              >
                {!props.readonly && (
                  <Input
                    checked={props.selectedDeliveryOption === option.id}
                    className="h-4 w-4 cursor-pointer accent-stone-900 shadow-none outline-none"
                    id={`delivery-${option.id}`}
                    name="delivery-option"
                    onChange={() => props.onDeliveryOptionChange?.(option.id)}
                    type="radio"
                    value={option.id}
                  />
                )}
                <span className="text-sm text-stone-900">{option.label}</span>
              </Label>
            ))}
          </div>
        </div>

        {/* Payment Method */}
        {props.paymentMethods && props.paymentMethods.length > 0 ? (
          <div className="flex flex-col gap-2">
            <Label className="font-semibold text-base">
              {props.microcopies.paymentMethodLabel}
            </Label>
            {props.microcopies.paymentOptionsDescription && (
              <div className="max-w-[210px]">
                <Text variant={"body-sm"}>
                  {props.microcopies.paymentOptionsDescription}
                </Text>
              </div>
            )}
            {/* Radio Options */}
            <div className="flex flex-col gap-2">
              {props.paymentMethods.map((method) => (
                <Label
                  className="flex cursor-pointer items-center gap-2 font-normal"
                  htmlFor={`payment-${method.id}`}
                  key={method.id}
                >
                  {!props.readonly && (
                    <Input
                      checked={props.selectedPaymentMethod === method.id}
                      className="h-4 w-4 cursor-pointer accent-stone-900 shadow-none outline-none"
                      id={`payment-${method.id}`}
                      name="payment-method"
                      onChange={() => props.onPaymentMethodChange?.(method.id)}
                      type="radio"
                      value={method.id}
                    />
                  )}
                  <span className="text-sm text-stone-900">{method.label}</span>
                </Label>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {showOptionalSection && (
        <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2">
          {/* Left Half - Optional Fields */}
          <div className="flex flex-col gap-5 md:flex-row md:gap-10">
            {/* Personal Reference */}
            {props.onPersonalReferenceChange ? (
              <div className="flex flex-1 flex-col gap-2">
                <Label className="font-semibold text-base">
                  {props.microcopies.optionalLabel}
                </Label>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="personal-reference">
                    {props.microcopies.personalReferenceLabel}
                  </Label>
                  <Input
                    id="personal-reference"
                    maxLength={personalReferenceMaxLength}
                    onChange={handleReferenceChange}
                    placeholder={props.microcopies.personalReferencePlaceholder}
                    type="text"
                    value={internalReference}
                  />
                  <Label className="font-normal text-stone-500 text-xs">
                    {props.microcopies.characterCounter
                      .replace("{current}", String(internalReference.length))
                      .replace("{max}", String(personalReferenceMaxLength))}
                  </Label>
                </div>
              </div>
            ) : null}

            {/* Requested Delivery Date */}
            {props.onDeliveryDateChange ? (
              <div className="flex flex-1 flex-col gap-2">
                <Label className="font-semibold text-base">
                  {props.microcopies.requestedShippingDateLabel}
                </Label>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="delivery-date">
                    {props.microcopies.dateLabel}
                  </Label>
                  <DatePicker
                    className="w-full"
                    country={props.country}
                    data-testid={
                      props["data-testid"]
                        ? `${props["data-testid"]}-delivery-date`
                        : undefined
                    }
                    fromDate={props.minDeliveryDate}
                    locale={props.locale}
                    onDateChange={props.onDeliveryDateChange}
                    selectedDate={props.requestedDeliveryDate}
                    toDate={props.maxDeliveryDate}
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* Right Half - Note to Customer Service */}
          {props.onOrderNoteChange ? (
            <div className="flex flex-col gap-2">
              <Label className="font-semibold text-base">
                {props.microcopies.noteToCustomerServiceLabel}
              </Label>
              <div className="flex flex-col gap-1.5">
                <textarea
                  className="min-h-[100px] w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm shadow-sm outline-none transition-colors placeholder:text-stone-400 focus-visible:border-stone-900 focus-visible:ring-1 focus-visible:ring-stone-900"
                  id="order-note"
                  maxLength={255}
                  onChange={handleNoteChange}
                  placeholder=""
                  value={internalNote}
                />
                <div className="flex flex-col gap-1">
                  <Label className="font-normal" variant="muted">
                    {props.microcopies.noteToCustomerServiceDescription}
                  </Label>
                  <Label className="font-normal text-stone-500 text-xs">
                    {props.microcopies.characterCounter
                      .replace("{current}", String(internalNote.length))
                      .replace("{max}", "255")}
                  </Label>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
